import React, { useState } from "react";
import AdminDataTable from "../components/AdminDataTable";
import Button from "../../../components/ui/Button";
import { Plus, Trash2, Shield, Eye } from "lucide-react";
import CreateUserModal from "../components/CreateUserModal";
import { useData } from "../../../context/DataContext";

const SystemUsersPage = () => {
  const { systemUsers, systemRoles, addSystemUser, deleteSystemUser, logAction } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const columns = [
    {
      header: "User",
      key: "name",
      width: "240px",
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold">
            {row.name.charAt(0)}
          </div>
          <div>
             <p className="font-medium text-slate-800">{row.name}</p>
             <p className="text-xs text-slate-500">{row.email}</p>
          </div>
        </div>
      ),
      sortable: true
    },
    { 
      header: "Role", 
      key: "role", 
      width: "150px",
      sortable: true, 
      render: (r) => <span className="flex items-center gap-1 text-slate-600"><Shield size={14} /> {r.role}</span> 
    },
    { 
      header: "Last Login", 
      key: "lastLogin", 
      width: "180px",
      sortable: true 
    },
    { 
      header: "Status", 
      key: "status", 
      width: "120px",
      sortable: true,
      render: (row) => (
         <span className={`px-2 py-1 text-xs font-semibold rounded-full ${row.status === "Active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
            {row.status}
         </span>
      )
    },
    {
      header: "Actions",
      key: "actions",
      width: "100px",
      render: (row) => (
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-8 h-8 p-0 text-slate-400 hover:text-purple-600 hover:bg-purple-50"
            onClick={() => {/* View logic if needed */}}
            title="View Details"
          >
            <Eye size={16} />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-8 h-8 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50" 
            onClick={() => {
                deleteSystemUser(row.id);
                logAction({
                    action: "DELETE_SYSTEM_USER",
                    targetId: row.id,
                    targetName: row.name,
                    actorName: "Super Admin",
                    details: "Removed system access"
                });
            }}
            title="Remove Access"
          >
            <Trash2 size={16} />
          </Button>
        </div>
      )
    }
  ];

  const handleCreate = (data) => {
    addSystemUser(data);
    logAction({
        action: "ADD_SYSTEM_USER",
        targetName: data.name,
        actorName: "Super Admin",
        details: `Invited new system user as ${data.role}`
    });
    setIsModalOpen(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">System Users</h1>
          <p className="text-slate-500 mt-1">Manage admin access and internal team members.</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
           <Plus className="mr-2 h-4 w-4" /> Add User
        </Button>
      </div>

      <AdminDataTable 
        title="Internal Staff" 
        columns={columns} 
        data={systemUsers}
        searchableFields={["name", "email", "role"]}
        filterableFields={[
            { key: "role", label: "Role", options: systemRoles.map(r => r.name) },
            { key: "status", label: "Status", options: ["Active", "Inactive"] }
        ]}
      />

      <CreateUserModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSave={handleCreate}
        roles={systemRoles}
      />
    </div>
  );
};

export default SystemUsersPage;
